/**
 * App-owned authorization model.
 *
 * Google SSO proves *who* someone is. Everything in this file is what the
 * application itself decides they may *do* — it is stored in Cosmos by
 * dave-service and re-read on every protected request, so revoking access takes
 * effect immediately rather than at session expiry.
 */

export const ROLES = ["admin", "developer", "user", "read_only"] as const;
export type Role = (typeof ROLES)[number];

export const USER_STATUSES = ["invited", "active", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Administrator",
  developer: "Developer",
  user: "Standard user",
  read_only: "Read only",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: "Full business access, including user management, invites and audit logs. Developer tools are assigned separately.",
  developer: "Full maintenance access, including every admin area and protected development tools.",
  user: "Day-to-day operations: quotes, leads, OGI retrieval and step forms.",
  read_only: "Can view operational data but cannot change anything.",
};

/* -------------------------------------------------------------------------- */
/* Permissions                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Permission names, not role comparisons. Guard on these everywhere so a role
 * change is one edit to ROLE_PERMISSIONS rather than a repo-wide grep.
 * The same table is duplicated server-side in dave-service `Auth/Permissions.cs`
 * — the client copy only decides what to *render*.
 */
export const PERMISSIONS = [
  "quotes.view",
  "quotes.search",
  "quotes.email.send",
  "leads.view",
  "ogi.retrieve",
  "stepforms.use",
  "users.manage",
  "invites.manage",
  "audit.view",
  "partners.manage",
  "noetica.retry",
  "devtools.use",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS.filter((permission) => permission !== "devtools.use"),
  developer: PERMISSIONS,
  user: [
    "quotes.view",
    "quotes.search",
    "leads.view",
    "ogi.retrieve",
    "stepforms.use",
  ],
  read_only: ["quotes.view", "quotes.search", "leads.view"],
};

export function permissionsForRole(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/* -------------------------------------------------------------------------- */
/* Records                                                                     */
/* -------------------------------------------------------------------------- */

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  picture?: string | null;
  role: Role;
  status: UserStatus;
  developer_tools_enabled?: boolean;
  invited_by?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
  last_login_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Invite {
  id: string;
  email: string;
  role: Role;
  invited_by: string;
  expires_at: string;
  accepted_at?: string | null;
  revoked_at?: string | null;
  created_at: string;
  updated_at: string;
}

export type InviteStatus = "pending" | "accepted" | "revoked" | "expired";

export function inviteStatus(invite: Invite, now = new Date()): InviteStatus {
  if (invite.accepted_at) return "accepted";
  if (invite.revoked_at) return "revoked";
  if (new Date(invite.expires_at).getTime() <= now.getTime()) return "expired";
  return "pending";
}

export const AUDIT_ACTIONS = [
  "auth.login",
  "auth.logout",
  "auth.denied",
  "user.self_registered",
  "user.invited",
  "user.invite_accepted",
  "user.invite_revoked",
  "user.invite_resent",
  "user.role_updated",
  "user.developer_tools_updated",
  "user.disabled",
  "user.enabled",
  "partner.created",
  "partner.enabled",
  "partner.disabled",
  "partner.updated",
  "partner.key_rotated",
  "noetica.retried",
  "quote.customer_email_queued",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number] | (string & {});

export interface AuditLogEntry {
  id: string;
  actor_email: string;
  actor_user_id?: string | null;
  action: AuditAction;
  resource_type: string;
  resource_id?: string | null;
  metadata?: Record<string, unknown> | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
}

/* -------------------------------------------------------------------------- */
/* Session                                                                     */
/* -------------------------------------------------------------------------- */

/** The minimum safe claim set the browser is allowed to hold. */
export interface SessionUser {
  id: string;
  email: string;
  name?: string | null;
  picture?: string | null;
  role: Role;
  status: UserStatus;
  developer_tools_enabled?: boolean;
  permissions: Permission[];
}

export interface SessionResponse {
  authenticated: boolean;
  user: SessionUser | null;
  /** ISO timestamp at which this session stops being valid. */
  expires_at?: string | null;
}

export type AuthDenialReason =
  | "email_unverified"
  | "domain_not_allowed"
  | "invite_required"
  | "invite_invalid"
  | "invite_expired"
  | "user_disabled"
  | "unknown";

export const AUTH_DENIAL_MESSAGES: Record<AuthDenialReason, string> = {
  email_unverified:
    "Your Google account does not have a verified email address.",
  domain_not_allowed:
    "That email domain is not allowed to access the admin dashboard.",
  invite_required:
    "Admin access is invite-only. Ask an administrator to invite you.",
  invite_invalid: "That invite link is not valid. It may already have been used.",
  invite_expired: "That invite link has expired. Ask for a new one.",
  user_disabled: "Your access has been disabled. Contact an administrator.",
  unknown: "We could not sign you in. Please try again.",
};

/* -------------------------------------------------------------------------- */
/* Admin management payloads                                                   */
/* -------------------------------------------------------------------------- */

export interface CreateInviteRequest {
  email: string;
  role: Role;
}

export interface BulkInviteRequest {
  invites: CreateInviteRequest[];
}

export interface BulkInviteResult {
  email: string;
  ok: boolean;
  error?: string;
}

export interface UpdateUserRoleRequest {
  role: Role;
}

export interface UpdateUserStatusRequest {
  status: Extract<UserStatus, "active" | "disabled">;
}
